import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useProducts } from "../hooks/useProducts";
import { SITE } from "../data/site";

const FILTERS = ["All", "1.1kV", "11kV", "22kV", "33kV", "66kV"];

const Products = () => {
  const { data: products } = useProducts();
  const [filter, setFilter] = useState("All");
  const visible = products.filter(
    (c) => filter === "All" || (c.voltages || []).some((v) => v.includes(filter))
  );

  return (
    <>
      <Seo
        title="Products | 3M Cable Jointing Kits, Terminations & Accessories — Universal Engineers Delhi"
        description="Browse the full 3M cable accessories range: jointing kits & terminations 1.1kV–66kV, right angle boots, electrical tapes, HT tapes, epoxies, anti-tracking HT sleeves and ACSR conductor covers — ready stock in Delhi, PAN India supply."
        keywords="3M cable jointing kits India, 33kV termination kit supplier, 11kV cable jointing kit Delhi, right angle boots 11kV supplier, electrical tape dealer Delhi, HT self-amalgamating tape, epoxy resin cable jointing, anti-tracking sleeve supplier, ACSR conductor cover supplier, LT HT cable accessories India"
        path="/products"
        jsonld={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "3M Cable Accessories Product Categories",
          itemListElement: products.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            url: `${SITE.domain}/products/${c.slug}`,
          })),
        }}
      />

      <section className="grain relative overflow-hidden bg-navy pb-20 pt-36 sm:pb-24 lg:pt-44" data-testid="products-hero">
        <div className="bp-grid absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 sm:text-xs">
              <span className="inline-block h-2 w-2 bg-safety" /> Product Range
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Engineered accessories. Every voltage class.
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Category-level depth first — genuine 3M systems, honest
              specifications and stock that leaves Delhi within a day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="products-catalog">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap gap-2.5">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`voltage-filter-pill-${f.replace(/[^0-9a-z]/gi, "")}`}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] transition-all ${
                  filter === f
                    ? "border-safety bg-safety text-white"
                    : "border-slate-300 text-slate-600 hover:border-navy hover:text-navy"
                }`}
              >
                {f}
              </button>
            ))}
          </Reveal>

          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" delay={0.03}>
            {visible.map((c) => (
              <StaggerItem key={c.slug}>
                <Link
                  to={`/products/${c.slug}`}
                  data-testid={`catalog-card-${c.slug}`}
                  className="group flex h-full flex-col border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-safety hover:shadow-xl"
                >
                  <div className="relative overflow-hidden bg-navy">
                    <img src={c.image} alt={c.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute left-3 top-3 bg-safety px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-white" data-testid="catalog-stock-badge">
                      Ready Stock · Delhi
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex flex-wrap gap-1.5">
                      {c.voltages.slice(0, 4).map((v) => (
                        <span key={v} className="bg-slate-100 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-slate-600">
                          {v}
                        </span>
                      ))}
                    </div>
                    <h2 className="mt-3 font-display text-xl font-bold tracking-tight text-navy">{c.name}</h2>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{c.short}</p>
                    <span className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-navy transition-colors group-hover:text-safety">
                      View range <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
};

export default Products;
